import React from 'react';
import {trim} from 'lodash';
import {toStringId} from '@packages/systems/core/utils/HtmlUtils';
import {
  Expressions,
  withSystem,
  withMeta,
  Plugins,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionEditor,
  updateExpressionsAction,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  TRecord,
  TText,
  TEnum,
  TTypeConstructor,
  EText,
} from '@packages/systems/wfdl-ast';
import {
  FORM_NAME_PATH,
  INPUT_TYPE_PATH,
  ATTR_REQUIRED_PATH,
  ATTR_ID_PATH,
  ATTR_CHECKED_PATH,
  ATTR_NAME_PATH,
  ATTR_DATA_NAME_PATH,
  CHECKBOX_DEFAULT,
  CHECKBOX_DEFAULT_CAP,
} from '../constants';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {
  usysFieldType,
  USYS_CONTEXT_PATH,
} from '@packages/systems/users/usysWfdl';
import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';

const {getIn} = ContextualExpressions;

const BottomMargin = emotionStyled('div')({
  marginBottom: tokens.space[2],
});

const formFields = TRecord({
  type: withSystem({defaultValue: 'checkbox-input', private: true}, TText),
  name: withMeta(
    {placeholder: 'e.g. Checkbox'},
    withSystem(
      {label: 'Name', defaultValue: CHECKBOX_DEFAULT_CAP, isLocalizable: true},
      TTypeConstructor(qualify(Plugins.name('Basic'), typeName('OnBlurText')))
    )
  ),
});

const inputTypeFields = withSystem(
  {
    defaultValue: 'default',
    label: 'Style',
    isLocalizable: true,
  },
  TEnum({default: 'Default', custom: 'Custom'})
);

const attrFields = TRecord({
  type: withSystem({defaultValue: CHECKBOX_DEFAULT, private: true}, TText),
  name: withSystem(
    {defaultValue: CHECKBOX_DEFAULT_CAP, private: true, isLocalizable: true},
    TText
  ),
  id: withSystem(
    {defaultValue: CHECKBOX_DEFAULT, private: true, isLocalizable: true},
    TText
  ),
  'data-name': withSystem(
    {defaultValue: CHECKBOX_DEFAULT_CAP, private: true, isLocalizable: true},
    TText
  ),
  required: withSystem(
    {labelVariant: 'hidden', label: 'Required', isLocalizable: true},
    TTypeConstructor(
      qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
    )
  ),
  checked: withSystem(
    {labelVariant: 'hidden', label: 'Start checked', isLocalizable: true},
    TTypeConstructor(
      qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
    )
  ),
  placeholder: TText,
});

const type = {
  users: usysFieldType,
  form: formFields,
  inputType: inputTypeFields,
  attr: attrFields,
} as const;

function RenderFormCheckboxDataType({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: (arg1?: any) => void;
}) {
  const onChangeAt = React.useCallback(
    (at: ExpressionPath) => extendExpressionEditorActionAtPath(dispatch)(at),
    [dispatch]
  );

  const onNameCommit = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter 'dispatchExpr' implicitly has an 'any' type.
    (dispatchExpr) => {
      const newName =
        Expressions.getText(dispatchExpr?.payload?.newExpression) || '';
      const trimmedName = EText(trim(newName));
      const idString = EText(toStringId(newName) || '');
      dispatch(
        updateExpressionsAction([
          {key: ATTR_ID_PATH, newExpression: idString},
          {key: ATTR_NAME_PATH, newExpression: idString},
          {key: ATTR_DATA_NAME_PATH, newExpression: trimmedName},
          {key: FORM_NAME_PATH, newExpression: trimmedName},
        ])
      );
    },
    [dispatch]
  );

  const expressionEditorAtPath = React.useCallback(
    (path: ExpressionPath) => {
      const exprAt = getIn(expr, path);
      if (!exprAt) {
        return null;
      }
      const onDispatch =
        path === FORM_NAME_PATH ? onNameCommit : onChangeAt(path);
      return <ExpressionEditor expression={exprAt} dispatch={onDispatch} />;
    },
    [expr, onChangeAt, onNameCommit]
  );
  const InputType = expressionEditorAtPath(INPUT_TYPE_PATH);
  const usysContext = getIn(expr, USYS_CONTEXT_PATH);
  const usysContextValue =
    usysContext && ContextualExpressions.getEnum(stripAnn(usysContext));
  if (usysContextValue === 'userAccount') {
    return <>{InputType}</>;
  }

  const Required = expressionEditorAtPath(ATTR_REQUIRED_PATH);
  const Checked = expressionEditorAtPath(ATTR_CHECKED_PATH);

  return (
    <>
      <BottomMargin>{InputType}</BottomMargin>
      {Required}
      {Checked}
    </>
  );
}

const UserFormCheckboxType = createDevLinkRootAlias({
  fields: type,
  params: ['extraAtomFields'],
  render: RenderFormCheckboxDataType,
});

export default UserFormCheckboxType;
